export type MapsPlace = {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
};

export class MapsUrlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MapsUrlError";
  }
}

function decodePlace(value: string) {
  return decodeURIComponent(value.replace(/\+/g, " ")).replace(/\s+/g, " ").trim();
}

function looksLikeAddress(value: string) {
  return value.includes(",") || /\d/.test(value);
}

function coordinatePair(value: string) {
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude: match[1], longitude: match[2] };
}

export function parseGoogleMapsUrl(raw: string): MapsPlace {
  const text = String(raw || "").trim();
  if (!text) throw new MapsUrlError("Paste a Google Maps link.");
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    throw new MapsUrlError("Paste a full Google Maps link.");
  }
  const host = url.hostname.replace(/^www\./, "");
  if (host === "maps.app.goo.gl" || host === "goo.gl") {
    throw new MapsUrlError("Open the short link, then paste the full Google Maps address from the browser.");
  }
  if (!host.endsWith("google.com") && host !== "maps.google.com") {
    throw new MapsUrlError("Paste a Google Maps link.");
  }

  const place: MapsPlace = { name: "", address: "", latitude: "", longitude: "" };
  const placeMatch = url.pathname.match(/\/place\/([^/]+)/) || url.pathname.match(/\/search\/([^/]+)/);
  if (placeMatch) {
    const title = decodePlace(placeMatch[1]);
    const comma = title.indexOf(",");
    if (comma > 0) {
      place.name = title.slice(0, comma).trim();
      place.address = title.slice(comma + 1).trim();
    } else if (looksLikeAddress(title) && !place.name) {
      place.address = title;
    } else {
      place.name = title;
    }
  }

  const at = url.pathname.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) {
    place.latitude = at[1];
    place.longitude = at[2];
  }
  const dataLat = url.href.match(/!3d(-?\d+(?:\.\d+)?)/);
  const dataLng = url.href.match(/!4d(-?\d+(?:\.\d+)?)/);
  if (dataLat && dataLng) {
    place.latitude = dataLat[1];
    place.longitude = dataLng[1];
  }

  const query = url.searchParams.get("query") || url.searchParams.get("q") || "";
  if (query) {
    const pair = coordinatePair(query);
    if (pair) {
      place.latitude = place.latitude || pair.latitude;
      place.longitude = place.longitude || pair.longitude;
    } else {
      const decoded = decodePlace(query);
      if (looksLikeAddress(decoded)) place.address = place.address || decoded;
      else place.name = place.name || decoded;
    }
  }

  if (!place.name && !place.address && !place.latitude) {
    throw new MapsUrlError("This link has no place name, address, or coordinates.");
  }
  return place;
}
